import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserX, UserCheck, Pencil, Search, Trash2 } from "lucide-react";
import { adminApi } from "@/lib/api/admin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { getInitials } from "@/lib/get-initials";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import { EditUserDialog } from "./EditUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";

interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  isActive: boolean;
  isGlobalAdmin: boolean;
  canDelete: boolean;
  createdAt: string;
  updatedAt: string;
}

export function UserList() {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => adminApi.listUsers(),
  });

  const deactivateUser = useMutation({
    mutationFn: adminApi.deactivateUser,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const reactivateUser = useMutation({
    mutationFn: adminApi.reactivateUser,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] }),
  });

  const deleteUser = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setDeletingUser(null);
    },
    onError: (error: Error) => {
      toast(error?.message || "Failed to delete user");
      setDeletingUser(null);
    },
  });

  const users = data?.users ?? [];

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user: AdminUser) =>
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        Loading users...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-destructive">
        Failed to load users
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search filter */}
      <div className={isMobile ? "relative" : "relative max-w-sm"}>
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search users by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 md:pl-9"
        />
      </div>

      {/* User list */}
      {filteredUsers.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          {searchQuery ? "No users match your search." : "No users found."}
        </div>
      ) : (
        <ul className="list-none space-y-2">
          {filteredUsers.map((user: AdminUser) => (
            <li
              key={user.id}
              className={isMobile
                ? "rounded-xl bg-white/[0.03] p-3 space-y-2"
                : "rounded-lg border p-4 flex items-center justify-between"
              }
            >
              <div className="flex items-center gap-3">
                <Avatar className={isMobile ? "h-10 w-10" : ""}>
                  {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
                  <AvatarFallback>
                    {getInitials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className={cn("font-medium truncate", isMobile && "text-sm")}>{user.name}</p>
                  <p className={cn("text-muted-foreground truncate", isMobile ? "text-xs" : "text-sm")}>{user.email}</p>
                  {isMobile && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {user.isGlobalAdmin && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">Admin</Badge>
                      )}
                      <Badge variant={user.isActive ? "default" : "destructive"} className="text-xs px-1.5 py-0">
                        {user.isActive ? "Active" : "Deactivated"}
                      </Badge>
                    </div>
                  )}
                </div>
                {isMobile && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditingUser(user)}
                      title="Edit user"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!user.isGlobalAdmin && (
                      <>
                        {user.isActive ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => deactivateUser.mutate(user.id)}
                            disabled={deactivateUser.isPending}
                            title="Deactivate user"
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => reactivateUser.mutate(user.id)}
                            disabled={reactivateUser.isPending}
                            title="Reactivate user"
                          >
                            <UserCheck className="h-4 w-4" />
                          </Button>
                        )}
                        {user.canDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeletingUser(user)}
                          title="Delete user"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              {!isMobile && (
              <div className="flex items-center gap-2">
                {user.isGlobalAdmin && (
                  <Badge variant="secondary">Admin</Badge>
                )}
                <Badge variant={user.isActive ? "default" : "destructive"}>
                  {user.isActive ? "Active" : "Deactivated"}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingUser(user)}
                  title="Edit user"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {!user.isGlobalAdmin && (
                  <>
                    {user.isActive ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deactivateUser.mutate(user.id)}
                        disabled={deactivateUser.isPending}
                        title="Deactivate user"
                      >
                        <UserX className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => reactivateUser.mutate(user.id)}
                        disabled={reactivateUser.isPending}
                        title="Reactivate user"
                      >
                        <UserCheck className="h-4 w-4" />
                      </Button>
                    )}
                    {user.canDelete && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeletingUser(user)}
                      title="Delete user"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    )}
                  </>
                )}
              </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Edit dialog */}
      <EditUserDialog
        user={editingUser}
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
      />

      {/* Delete confirmation dialog */}
      <DeleteUserDialog
        user={deletingUser}
        open={!!deletingUser}
        onClose={() => setDeletingUser(null)}
        onConfirm={() => deletingUser && deleteUser.mutate(deletingUser.id)}
        isDeleting={deleteUser.isPending}
      />
    </div>
  );
}
